import React, { useState, useEffect } from 'react';
import { FiUser, FiLock, FiMail, FiSave, FiCheck, FiShield, FiSmartphone, FiTrash2 } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import { useResponseModal } from '../../contexts/ResponseModalContext';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';

function MinhaConta() {
  const { showSuccess, showError } = useResponseModal();
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState({
    name: '',
    email: '',
    role: ''
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [loading, setLoading] = useState(false);

  // Estado para 2FA
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [password2FA, setPassword2FA] = useState('');
  const [loading2FA, setLoading2FA] = useState(false);

  // Carregar status do 2FA
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const response = await authService.get2FAStatus();
        setIs2FAEnabled(response.data?.totp_enabled || false);
      } catch {
        // Ignorar erro ao carregar status
      }
    };
    load2FAStatus();
  }, []);

  const handleSetup2FA = async () => {
    setLoading2FA(true);
    try {
      const response = await authService.setup2FA();
      if (response.success) {
        setSetupData(response.data);
        setShow2FASetup(true);
        setVerifyCode('');
      } else {
        showError(response.message || 'Erro ao iniciar configuração', 'Erro');
      }
    } catch (error) {
      showError('Erro ao iniciar configuração 2FA', 'Erro');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleConfirm2FA = async (e) => {
    e.preventDefault();
    if (!setupData) return;
    setLoading2FA(true);
    try {
      const response = await authService.confirm2FA({
        secret: setupData.secret,
        code: verifyCode
      });
      if (response.success) {
        showSuccess('Autenticação de dois fatores ativada com sucesso!', '2FA Ativado');
        setIs2FAEnabled(true);
        setShow2FASetup(false);
        setSetupData(null);
        setVerifyCode('');
      } else {
        showError(response.message || 'Código inválido', 'Erro');
      }
    } catch (error) {
      showError(error.response?.data?.error || 'Código de verificação inválido', 'Erro');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (!password2FA) {
      showError('Digite sua senha para desativar o 2FA', 'Senha Necessária');
      return;
    }
    setLoading2FA(true);
    try {
      const response = await authService.disable2FA({ password: password2FA });
      if (response.success) {
        showSuccess('Autenticação de dois fatores desativada.', '2FA Desativado');
        setIs2FAEnabled(false);
        setPassword2FA('');
      } else {
        showError(response.message || 'Senha incorreta', 'Erro');
      }
    } catch (error) {
      showError(error.response?.data?.error || 'Erro ao desativar 2FA', 'Erro');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleCancel2FASetup = () => {
    setShow2FASetup(false);
    setSetupData(null);
    setVerifyCode('');
  };

  // Carregar dados do usuário logado
  useEffect(() => {
    if (authUser) {
      setUser({
        name: authUser.name || '',
        email: authUser.email || '',
        role: authUser.role || ''
      });
    }
  }, [authUser]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.updateProfile({
        name: user.name,
        email: user.email
      });

      if (response.success) {
        // Atualizar dados do usuário no AuthContext
        updateUser(response.data.user);
        showSuccess('Perfil atualizado com sucesso!', 'Perfil Salvo');
      } else {
        showError(response.message || 'Erro ao atualizar perfil', 'Erro');
      }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      showError('Erro ao atualizar perfil. Tente novamente.', 'Erro');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwords.new !== passwords.confirm) {
      showError('As senhas não coincidem. Verifique e tente novamente.', 'Senha Inválida');
      setLoading(false);
      return;
    }

    if (passwords.new.length < 6) {
      showError('A nova senha deve ter pelo menos 6 caracteres.', 'Senha Curta Demais');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.changePassword({
        current_password: passwords.current,
        new_password: passwords.new
      });

      if (response.success) {
        showSuccess('Senha alterada com sucesso!', 'Senha Atualizada');
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        showError(response.message || 'Erro ao alterar senha', 'Erro');
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      showError('Erro ao alterar senha. Tente novamente.', 'Erro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h2 className="mb-1">Minha Conta</h2>
        <p className="text-muted mb-0">Gerencie suas informações pessoais e senha.</p>
      </div>

      <div className="settings-container">
     
        {/* Conteúdo: Perfil */}
        <div className="settings-card-wrapper">
          <div className="card-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                  <FiUser size={24} />
                </div>
                <div>
                  <h5 className="mb-0 fw-bold text-dark">Informações Pessoais</h5>
                  <p className="mb-0 text-muted small">Atualize seus dados pessoais</p>
                </div>
              </div>
            </div>
            <div className="card-body-modern p-4">
              <form onSubmit={handleProfileSubmit}>
                <div className="row g-3">
                  <div className="col-12">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">
                        <FiUser className="me-2" />
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        className="form-control-modern"
                        value={user.name}
                        onChange={(e) => setUser({ ...user, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">
                        <FiMail className="me-2" />
                        E-mail
                      </label>
                      <input
                        type="email"
                        className="form-control-modern"
                        value={user.email}
                        onChange={(e) => setUser({ ...user, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">Cargo/Função</label>
                      <input
                        type="text"
                        className="form-control-modern"
                        value={user.role}
                        disabled
                        style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                      />
                      <small className="text-muted">Este campo não pode ser editado</small>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-top">
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={loading}
                  >
                    <FiSave className="me-2" />
                    {loading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Conteúdo: Autenticação de Dois Fatores */}
        <div className="settings-card-wrapper">
          <div className="card-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                  <FiShield size={24} />
                </div>
                <div>
                  <h5 className="mb-0 fw-bold text-dark">Autenticação de Dois Fatores</h5>
                  <p className="mb-0 text-muted small">Proteja sua conta com um aplicativo autenticador</p>
                </div>
              </div>
            </div>
            <div className="card-body-modern p-4">

              {show2FASetup && setupData ? (
                /* Etapa 2: Escanear QR Code e verificar */
                <div>
                  <div className="text-center mb-4">
                    <div style={{
                      display: 'inline-block', padding: '12px', background: '#fff',
                      borderRadius: '12px', border: '2px solid #e2e8f0'
                    }}>
                      <QRCodeSVG value={setupData.qr_code_uri} size={200} />
                    </div>
                    <p className="mt-3 mb-1" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                      Escaneie o QR Code com seu aplicativo autenticador
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      Google Authenticator, Authy ou similar
                    </p>
                  </div>

                  <div className="text-center mb-3">
                    <small className="text-muted">Ou digite a chave manualmente:</small>
                    <div className="mt-1">
                      <code style={{
                        fontSize: '0.8rem', background: '#f1f5f9', padding: '4px 12px',
                        borderRadius: '6px', wordBreak: 'break-all', userSelect: 'all'
                      }}>
                        {setupData.secret}
                      </code>
                      <button
                        type="button"
                        className="btn btn-link btn-sm ms-1"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => { navigator.clipboard.writeText(setupData.secret); showSuccess('Chave copiada!', 'Copiado'); }}
                      >
                        Copiar
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleConfirm2FA}>
                    <div className="form-group-modern">
                      <label className="fw-bold">Código de verificação</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control-modern text-center"
                        style={{ fontSize: '1.2rem', letterSpacing: '6px' }}
                        placeholder="000000"
                        maxLength={6}
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                        required
                        autoFocus
                      />
                      <small className="text-muted">Digite o código de 6 dígitos gerado pelo aplicativo</small>
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="submit"
                        className="btn-modern btn-modern-primary"
                        disabled={loading2FA || verifyCode.length < 6}
                      >
                        {loading2FA ? 'Verificando...' : 'Ativar 2FA'}
                      </button>
                      <button
                        type="button"
                        className="btn-modern btn-modern-secondary"
                        onClick={handleCancel2FASetup}
                        disabled={loading2FA}
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              ) : is2FAEnabled ? (
                /* Estado: 2FA ativado */
                <div>
                  <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <FiShield size={24} color="#16a34a" />
                    <div>
                      <strong style={{ color: '#16a34a' }}>Autenticação de dois fatores está ativa</strong>
                      <p className="mb-0 text-muted small">Sua conta está protegida com verificação em duas etapas.</p>
                    </div>
                  </div>

                  <form onSubmit={handleDisable2FA}>
                    <div className="form-group-modern">
                      <label className="fw-bold">Digite sua senha para desativar</label>
                      <div className="d-flex gap-2">
                        <input
                          type="password"
                          className="form-control-modern"
                          style={{ maxWidth: '300px' }}
                          placeholder="Sua senha atual"
                          value={password2FA}
                          onChange={(e) => setPassword2FA(e.target.value)}
                          required
                        />
                        <button
                          type="submit"
                          className="btn-modern btn-modern-danger"
                          disabled={loading2FA || !password2FA}
                        >
                          {loading2FA ? '...' : <><FiTrash2 className="me-1" />Desativar</>}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              ) : (
                /* Estado: 2FA desativado */
                <div>
                  <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded" style={{ background: '#fefce8', border: '1px solid #fde68a' }}>
                    <FiSmartphone size={24} color="#ca8a04" />
                    <div>
                      <strong style={{ color: '#ca8a04' }}>Autenticação de dois fatores está desativada</strong>
                      <p className="mb-0 text-muted small">Adicione uma camada extra de segurança à sua conta.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-modern btn-modern-primary"
                    onClick={handleSetup2FA}
                    disabled={loading2FA}
                  >
                    {loading2FA ? 'Preparando...' : <><FiShield className="me-2" />Configurar 2FA</>}
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Conteúdo: Alterar Senha */}
        <div className="settings-card-wrapper">
          <div className="card-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                  <FiLock size={24} />
                </div>
                <div>
                  <h5 className="mb-0 fw-bold text-dark">Alterar Senha</h5>
                  <p className="mb-0 text-muted small">Atualize sua senha de acesso</p>
                </div>
              </div>
            </div>
            <div className="card-body-modern p-4">
              <form onSubmit={handlePasswordSubmit}>
                <div className="alert alert-info mb-4">
                  <strong>Requisitos de senha:</strong>
                  <ul className="mb-0 mt-2 ps-3">
                    <li>Mínimo de 6 caracteres</li>
                    <li>A nova senha deve ser diferente da atual</li>
                  </ul>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">
                        <FiLock className="me-2" />
                        Senha Atual
                      </label>
                      <input
                        type="password"
                        className="form-control-modern"
                        value={passwords.current}
                        onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                        required
                        placeholder="Digite sua senha atual"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">
                        <FiLock className="me-2" />
                        Nova Senha
                      </label>
                      <input
                        type="password"
                        className="form-control-modern"
                        value={passwords.new}
                        onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                        required
                        minLength={6}
                        placeholder="Mínimo 6 caracteres"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label className="fw-bold">
                        <FiCheck className="me-2" />
                        Confirmar Nova Senha
                      </label>
                      <input
                        type="password"
                        className="form-control-modern"
                        value={passwords.confirm}
                        onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                        required
                        minLength={6}
                        placeholder="Repita a nova senha"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-top">
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={loading}
                  >
                    <FiSave className="me-2" />
                    {loading ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MinhaConta;
